/* The Golden Quest — campaign constants and copy (ported from the prototype).
   These are the defaults; api.js overlays whatever the campaign API sends. */

import artCard from './assets/art_card.webp';
import artVoucher from './assets/art_voucher.webp';
import artGold from './assets/art_gold.webp';
import artRef from './assets/art_ref.webp';

export const LAUNCH = new Date('2026-10-18T00:00:00+05:30');
export const END = new Date('2026-11-11T23:59:59+05:30');
/* reveal day: the grand draw, when the Gold Chests open (the API's drawOn) */
export const DRAW_ON = new Date('2026-11-13T20:00:00+05:30');

export const PRE_REG_GEMS = 5;
/* the pre-registration Gems' `earnedAt` fix, on the frontend on purpose: the
   API stamps them at launch midnight (1792261800000 — 18 Oct, 12:00 AM IST);
   a PRE_REG Gem carrying exactly that value is shown as earned 18 Oct, 6:00 PM
   (1792326600000). Any other value is shown as sent. */
export const PRE_REG_EARNED_AT_FROM = 1792261800000;
export const PRE_REG_EARNED_AT = new Date(1792326600000);
export const GRAND_PRIZE_COUNT = 23;

/* fallback when the API doesn't send the user's code */
export const REFERRAL_CODE = 'GSFEST-7K2';

export const shareMessageFor = (code) =>
  `I'm winning BTC, Gold and an iPone Duo, you can too with my code ${code}. https://dl.gosats.io/home`;

/* pre | live | ended from the campaign window */
export function phaseFromDates(launch, end, now = Date.now()) {
  if (now < launch.getTime()) return 'pre';
  if (now > end.getTime()) return 'ended';
  return 'live';
}

/* The four streaks. `art` is the tile render; `fine` is the small gold line
   under the description (not every streak has one). */
export const STREAKS = [
  { key: 'card', title: 'Card', desc: 'Your card already earns Bitcoin and Gold, it now earns gems with even greater prizes!', fine: 'On eligible card transactions', cta: 'Add Money', art: artCard, labels: ['₹0', '₹500', '₹1000', '₹1500', '₹2000'] },
  { key: 'voucher', title: 'Vouchers', desc: 'Shop the biggest brands and earn Gems.', fine: 'On eligible voucher purchases', cta: 'Buy Vouchers', art: artVoucher, labels: ['₹0', '₹500', '₹1000', '₹1500', '₹2000'] },
  { key: 'gold', title: 'Gold', desc: 'Buy gold, build wealth and earn gems', cta: 'Buy Gold', art: artGold, labels: ['₹0', '₹500', '₹1000', '₹1500', '₹2000'] },
  { key: 'ref', title: 'Referrals', desc: 'Refer a friend, when they make a card transaction you get gems.', cta: 'Refer a Friend', art: artRef, labels: ['0', '1', '2', '3', '4'] },
];

/* marquee over the top of the streak sheet (rendered twice for a seamless loop) */
export const TICKER = ['Stand a chance to win from a pool of mega prizes including an iPhone Duo', 'PlayStation 5', 'Apple Watch', 'Samsung phones', 'Apple AirPods', 'Bitcoin', 'Gold and so much more'];

/* left offset of the progress dot for 0..4 steps, and the matching scaleX of
   the 256px-wide fill bar. `pos` may be fractional (the amount so far between
   two Gems): the dot runs straight between the neighbouring anchors */
export const DOT_LEFT = [2, 49, 118, 185, 248];
export const dotLeft = (pos) => {
  const p = Math.max(0, Math.min(Number(pos) || 0, 4));
  const i = Math.min(Math.floor(p), 3);
  return +(DOT_LEFT[i] + (DOT_LEFT[i + 1] - DOT_LEFT[i]) * (p - i)).toFixed(2);
};
export const trackScale = (pos) => +((dotLeft(pos) + 8) / 256).toFixed(4);

/* where a streak's fill ends, in steps (0..4): the amount so far against the
   whole run (`steps × stepAmount` — ₹2000 on the spend streaks, 4 friends on
   referrals), never behind a Gem already credited (`progress`); the chest
   earned → full. Without an amount it falls back to the credited Gems. */
export function trackPosition({ amount, stepAmount, steps = 4, progress = 0, completed = false }) {
  if (completed) return 4;
  let pos = Math.max(0, Math.min(Number(progress) || 0, 4));
  const run = Number(steps) * Number(stepAmount);
  if (Number.isFinite(amount) && amount > 0 && run > 0) pos = Math.max(pos, (4 * amount) / run);
  return Math.max(0, Math.min(pos, 4));
}

/* streak title by key, for "Your Card streak is complete" style copy */
export const STREAK_NAME = Object.fromEntries(STREAKS.map((s) => [s.key, s.title]));

/* where a Gem came from (API `source` enum → label on the rewards page) */
export const GEM_SOURCE_LABEL = {
  PRE_REG: 'Pre-registration',
  CARD: 'Card streak',
  VOUCHER: 'Vouchers streak',
  GOLD: 'Gold streak',
  REFERRAL: 'Referral',
};

/* what an opened Gem shows while the backend has not decided its reward
   (it sends `reward: null` until reward selection is built) */
export const PENDING_REWARD = { name: 'Reward on its way', detail: 'Being credited to your account', kind: 'PENDING' };

/* the quick survey's Golden Chest, as it's listed with the streak chests */
export const SURVEY_CHEST_NAME = 'Survey bonus';
/* the Quote & Claim Golden Chest, likewise ("Quote & Claim bonus chest") */
export const QUOTE_CHEST_NAME = 'Quote & Claim bonus';

/* ---- Quote & Claim (X) ----
   The second "Bonus rewards" card: follow GoSats on X, quote our post, paste
   the post's link, and once the review team approves it a Golden Chest can
   be claimed. These are the defaults; api.js overlays `goldenQuest.quoteClaim`.
   `endsAt` is the last moment to submit a link; `postUrl` the post to quote
   ("Get started"), `profileUrl` the account to follow. */
export const QUOTE_CLAIM = {
  endsAt: new Date('2026-11-02T23:59:59+05:30'),
  handle: 'GoSats',
  profileUrl: 'https://x.com/GoSats',
  postUrl: 'https://x.com/GoSats',
};

/* every string the Quote & Claim UI shows, the fallback for what
   `quoteClaim.copy` leaves out. {date} / {time} / {day} / {handle} fill in. */
export const QUOTE_COPY = {
  title: 'Quote & Claim',
  text: 'Spread the word on X. Follow us and quote our post, drop the link here, and a chest is yours.',
  chip: 'Free Chest',
  go: 'Get started',
  paste: 'Paste your link',
  placeholder: 'Paste your post link',
  submit: 'Submit',
  reviewTitle: 'In review',
  reviewText: 'We\u2019ll review your post within 24 hours',
  reviewBy: 'We\u2019ll review it within 24 hours, by {date}, {time}',
  okTitle: 'Complete, Thank you!',
  okText: 'Your Golden Chest is ready.',
  claimedText: 'Golden Chest added \u00b7 opens {day}',
  claim: 'Claim Golden Chest',
  claimed: 'Claimed',
  endedTitle: 'This task has ended',
  endedText: 'Quote & Claim closed on {date}.',
  notify: 'Notify me about new tasks',
  notified: 'You\u2019ll be notified',
  note: 'Hear first when the next chest task goes live.',
  noteSet: 'We\u2019ll ping you when the next chest task goes live.',
  fabSub: 'Win an extra chest',
  fabRetry: 'Try again',
  fabReady: 'Chest ready',
  fabReadySub: 'Tap to claim it',
  submitted: 'Link submitted \u00b7 we\u2019ll review it within 24 hours',
  notifyOn: 'Reminder set \u00b7 we\u2019ll notify you about new tasks',
  notifyOff: 'Reminder turned off',
};

/* why the review team rejected a post (`quoteClaim.rejectReason`): the
   notice's title, text and, where one helps, an action link */
export const QUOTE_REJECT = {
  invalid: { title: 'Invalid or incorrect link', text: 'We couldn\u2019t open that post. Copy the link again from X and paste the full post link.' },
  wrongpost: { title: 'Wrong post quoted', text: 'Your post quotes a different post. Quote our GoSats post and share the new link.', action: 'Find our post', url: 'post' },
  notfollow: { title: 'You\u2019re not following {handle}', text: 'Follow @{handle} on X, then submit the same link again.', action: 'Follow @{handle}', url: 'profile' },
  deleted: { title: 'Post deleted or unavailable', text: 'We can\u2019t find your quote post anymore. Post a new one and share its link.' },
  private: { title: 'Your X account is private', text: 'We can\u2019t see posts from private accounts. Make your account public, then submit again.' },
  duplicate: { title: 'Already submitted', text: 'This link was already used, or this reward has already been claimed on your account.' },
};

/* every string the survey UI shows, the fallback for what `survey.copy`
   leaves out. "0 questions" is a count placeholder (survey.js fillCount). */
export const SURVEY_COPY = {
  title: 'Let\u2019s make your money work for you',
  text: 'Answer a few quick questions about your money habits and claim a free Golden Chest.',
  cta: 'Start survey',
  later: 'Maybe later',
  resumeTitle: 'You\u2019re 0 questions away from your Golden Chest',
  resumeText: 'Your answers are saved \u2014 pick up right where you left off.',
  resumeCta: 'Resume survey',
  resumeLater: 'Not now',
  doneTitle: 'Thanks for sharing! Your Golden Chest is claimed',
  doneCta: 'View Chests',
  doneText: 'Open it from your Chests',
  finishCta: 'Finish & claim your Chest',
  nextCta: 'Next',
  reward: 'A Golden Chest for finishing',
  duration: 'Under 2 minutes',
  endedTitle: 'This task has ended',
  endedText: 'The survey closed on {date}.',
  notify: 'Notify me about new tasks',
  notified: 'You\u2019ll be notified',
  note: 'Hear first when the next chest task goes live.',
  noteSet: 'We\u2019ll ping you when the next chest task goes live.',
  notifyOn: 'Reminder set \u00b7 we\u2019ll notify you about new tasks',
  notifyOff: 'Reminder turned off',
  chip: 'Finish to claim a Golden Chest',
  chipRemain: '0 questions to your Golden Chest',
  chipReady: 'Ready to claim your Golden Chest',
};

/* top three grand prizes shown on the hub; the full list lives in the prizes sheet */
export const GRAND_TOP = [
  { art: 'iphone', name: 'iPhone Duo', sub: '256 GB', win: '1st winner', badge: 'GRAND PRIZE' },
  { art: 'ps5', name: 'PlayStation 5', sub: 'Console', win: '2nd winner' },
  { art: 'watch2', name: 'Apple Watch SE', sub: 'GPS', win: '3rd winner' },
];

/* the full grand prize list (prizes sheet); `art` picks the product render */
export const GRAND_ALL = [
  { label: 'Grand 1', name: 'iPhone Duo', sub: '256 GB', winners: 1, art: 'iphone' },
  { label: 'Grand 2', name: 'PlayStation 5', sub: 'Console', winners: 1, art: 'ps5' },
  { label: 'Grand 3', name: 'Apple Watch SE', sub: 'GPS', winners: 1, art: 'watch2' },
  { label: 'Grand 4', name: 'Samsung Galaxy M17e', sub: 'Smartphone', winners: 1, art: 'samsung' },
  { label: 'Grand 5', name: 'Redmi Pad 2', sub: 'Tablet', winners: 1, art: 'redmi' },
  { label: 'Grand 6', name: 'AirPods 5', sub: 'Earbuds', winners: 1, art: 'airpods' },
  { label: 'Grand 7–8', name: 'OnePlus Watch 2R', sub: 'Smartwatch', winners: 2, art: 'opwatch' },
  { label: 'Grand 9–10', name: 'JBL Flip Essential 2', sub: 'Speaker', winners: 2, art: 'jblflip' },
  { label: 'Grand 11–12', name: 'Sony WH-CH520', sub: 'Wireless headphones', winners: 2, art: 'sony' },
  { label: 'Grand 13–14', name: 'JBL Go 4', sub: 'Speaker', winners: 2, art: 'jblgo' },
  { label: 'Grand 15–18', name: 'Fastrack Astor FR2', sub: 'Smartwatch', winners: 4, art: 'fastrack' },
  { label: 'Grand 19–21', name: 'OnePlus Nord Buds', sub: 'Earbuds', winners: 3, art: 'nordbuds' },
  { label: 'Grand 22–23', name: 'Ambrane 10000mAh', sub: 'Power bank', winners: 2, art: 'ambrane' },
];

/* How the hub's Grand Prizes card lays the list out (by prize name): one hero
   tile, a pair of tiles and four minis — the minis are too narrow for the full
   names, so each carries a short label. Any name the API doesn't send is
   filled from the rest of the list, in order. */
export const GRAND_CARD = {
  hero: 'iPhone Duo',
  pair: ['PlayStation 5', 'AirPods 5'],
  minis: [
    { name: 'Apple Watch SE', label: 'Apple Watch' },
    { name: 'Sony WH-CH520', label: 'Sony WH' },
    { name: 'JBL Flip Essential 2', label: 'JBL Flip' },
    { name: 'OnePlus Nord Buds', label: 'Nord Buds' },
  ],
};

/* How the prizes sheet lays the list out (by prize name): one hero card, two
   featured cards, the "Also up for grabs" grid, then plain rows. Anything the
   API sends that isn't named here falls into the rows. */
export const PRIZE_SHEET = {
  hero: 'iPhone Duo',
  featured: ['PlayStation 5', 'AirPods 5'],
  also: ['JBL Flip Essential 2', 'JBL Go 4', 'Apple Watch SE', 'Sony WH-CH520', 'OnePlus Nord Buds'],
  rows: ['Samsung Galaxy M17e', 'Redmi Pad 2', 'OnePlus Watch 2R', 'Fastrack Astor FR2', 'Ambrane 10000mAh'],
};

/* every other chest wins Sats or Gold, in tiers */
export const PRIZE_TIERS = [
  { code: 'C7', name: '1 gm Gold', winners: 2 },
  { code: 'C6', name: 'Sats or Gold', winners: 10 },
  { code: 'C5', name: 'Sats or Gold', winners: 60 },
  { code: 'C4', name: 'Sats or Gold', winners: 250 },
  { code: 'C3', name: 'Sats or Gold', winners: 560 },
  { code: 'C2', name: 'Sats or Gold', winners: 2127 },
  { code: 'C1', name: 'Sats or Gold', winners: 2968 },
];

/* The full terms, as numbered sections. Each section is a title plus blocks:
   { p } a paragraph, { ul } bullets (a bullet is a string or { lead, text }
   for a bold lead-in), { table: { head, rows } }. The sheet numbers the
   sections itself. `items` (a flat list of sentences) is the older shape
   the sheet still accepts. */
export const TERMS = {
  sections: [
    {
      title: 'About the contest',
      blocks: [
        { p: 'The Golden Quest (the "contest") is run by Saffroncoin Solutions Private Limited, operating as GoSats ("we", "us"). It is a contest with random selection. By opting in you accept these terms.' },
      ],
    },
    {
      title: 'Dates',
      blocks: [
        { p: 'All times are IST.' },
        { table: {
          head: ['What', 'When'],
          rows: [
            ['Pre-registration (opt-in) opens', 'Saturday 10 Oct 2026, 4:00 PM'],
            ['Early opt-in bonus', 'Opt in before the contest opens: from 10 Oct 2026, 4:00 PM to 18 Oct 2026, 6:00 PM'],
            ['Referrals bar opens', 'Tuesday 13 Oct 2026, 7:00 PM'],
            ['Contest period', '18 Oct 2026, 6:00 PM to 11 Nov 2026, [closing time to confirm]'],
            ['Reveal day (chests unlock)', '13 Nov 2026, [time to confirm]'],
          ],
        } },
      ],
    },
    {
      title: 'Who can take part',
      blocks: [
        { ul: [
          'GoSats users with completed KYC and access to the GoSats app.',
          '18 years or older and resident in India. Legal to confirm.',
        ] },
      ],
    },
    {
      title: 'How to join',
      blocks: [
        { ul: [
          'Opt in using the button or banner in the GoSats app. Enrolment is not automatic. Opting in is free.',
          'Early opt-in bonus: if you opt in before the contest opens, you get 5 gems. They are credited when you opt in and can be opened once the game goes live on 18 Oct. One bonus per user. Opt-ins after 18 Oct, do not get it.',
          'Spends and contest actions count only while the contest is live, from 18 Oct to its close on 11 Nov. The Referrals bar opens early: friends you refer count from 13 Oct, 7:00 PM. Nothing else before or after counts.',
        ] },
      ],
    },
    {
      title: 'Earning gems and Gold chests',
      blocks: [
        { p: 'There are four bars. Every bar works the same way: four gems fill a bar and earn one Gold chest.' },
        { table: {
          head: ['Bar', 'Earn a gem', 'Earn the Gold chest'],
          rows: [
            ['Card', 'Every ₹500 of eligible spends on the GoSats Card', '₹2,000 of eligible spends'],
            ['Vouchers', 'Every ₹500 spent on eligible vouchers', '₹2,000 on eligible vouchers'],
            ['Gold', 'Every ₹500 of Gold bought, one-time or through a SIP', '₹2,000 of Gold'],
            ['Referrals', '1 gem for each friend you refer', '4 friends'],
          ],
        } },
        { p: 'What counts and what does not:' },
        { ul: [
          'A referral counts only once the friend completes a card transaction, a Gold purchase or a voucher purchase. Referrals count from 13 Oct 2026, 7:00 PM, ahead of the other bars. Self-referrals and duplicate accounts do not count.',
          'The SuperCoin portion of any purchase does not count toward a bar.',
          'Wallet loads and wallet-style vouchers do not count.',
          'Eligible card spend categories and eligible voucher brands: [list to be finalised].',
          'Gold bought and then sold during the contest does not count.',
          'Each bar earns one Gold chest at most. The Card, Vouchers and Gold bars stop earning gems once they have given 4. The Referrals bar earns a gem for every friend, with no limit, and its Gold chest at 4 friends.',
        ] },
      ],
    },
    {
      title: 'Gems',
      blocks: [
        { ul: [
          'A gem is a small prize. It opens as soon as it is earned, once the game is live. The 5 early opt-in gems open from 18 Oct.',
          'Every gem has a prize inside: SATs or Gold, your pick, at the same rupee value either way.',
          'Gem prizes are credited to your GoSats account when you open the gem.',
        ] },
      ],
    },
    {
      title: 'Gold chests',
      blocks: [
        { ul: [
          'You earn a Gold chest when a bar is full.',
          'Chests are held until reveal day. They cannot be opened earlier.',
          'Every chest has a prize inside. Some hold devices, and the rest hold SATs or Gold.',
        ] },
      ],
    },
    {
      title: 'Prizes',
      blocks: [
        { ul: [
          { lead: 'Grand prizes:', text: 'devices, such as the iPhone Duo. They are not available as cash, SATs or Gold.' },
          { lead: '1 gm Gold prize:', text: 'some chests hold 1 gm of Gold. This prize is Gold only.' },
          { lead: 'Other chest prizes:', text: 'SATs or Gold, your pick, at the same rupee value either way.' },
          'SATs and Gold are valued in rupees at the rate when the prize is credited to your GoSats account.',
          'Device models, variants and colours depend on availability. We may replace a device with one of equal value. Images are for illustration. Warranty is provided by the manufacturer.',
          'Prizes cannot be transferred or exchanged for cash.',
        ] },
      ],
    },
    {
      title: 'Selection and winners',
      blocks: [
        { ul: [
          'Prizes in Gold chests are assigned by random selection on reveal day. The result is final.',
          'Winners are told in the GoSats app and by push notification.',
          'Device winners must share delivery details within 7 days or the prize is forfeited.',
          '[With your consent, we may show your name in short form and your city when winners are announced. Legal to confirm.]',
        ] },
      ],
    },
    {
      title: 'Tax and delivery',
      blocks: [
        { ul: [
          'Prizes are subject to tax as required by Indian law.',
          'Devices are delivered to addresses in India only.',
        ] },
      ],
    },
    {
      title: 'Fair play',
      blocks: [
        { p: 'We may remove gems, chests and prizes, and disqualify a user, for fake or duplicate accounts, self-referrals, round-trip Gold purchases, automated activity, or any attempt to get around these terms. Our decision is final.' },
      ],
    },
    {
      title: 'Changes and cancellation',
      blocks: [
        { p: 'We may change, pause or end the contest for fraud, technical failure or legal reasons. Changes will be shown in the app.' },
      ],
    },
    {
      title: 'Data',
      blocks: [
        { p: 'We use your data as set out in the GoSats Privacy Policy and only to run the contest.' },
      ],
    },
    {
      title: 'SATs and Gold',
      blocks: [
        { p: 'SATs are units of Bitcoin, which is a virtual digital asset. Gold in the contest is digital Gold held in your GoSats account.' },
      ],
    },
    {
      title: 'Governing law',
      blocks: [
        { p: 'These terms are governed by the laws of India.' },
      ],
    },
  ],
};

export const FAQS = [
  { q: 'What is The Golden Quest?', a: 'A GoSats contest that runs from 18 Oct, 6 PM, to 11 Nov. You fill four bars, earn gems and Gold chests, and open gems as you go. Gold chests unlock together on reveal day, 13 Nov. Some hold big prizes, starting with an iPhone Duo.' },
  { q: 'What is a gem?', a: 'A small prize. You earn one every time you cross ₹500 in a category, and you can open it straight away. Inside is SATs or Gold, your pick.' },
  { q: 'What is a Gold chest?', a: 'You earn one when a bar is full. It is held until reveal day. Every chest has a prize inside. Some hold devices, some hold 1 gm of Gold, and the rest hold SATs or Gold.' },
  { q: 'What are the four bars?', a: 'Card, Vouchers, Gold and Referrals. Each has its own bar and its own Gold chest.' },
  { q: 'How do I join?', a: 'Opt in from the GoSats app. Pre-registration opens on 10 Oct at 4 PM and is free.' },
  { q: 'What do I get for opting in early?', a: 'Opt in before the contest opens on 18 Oct at 6 PM and you get 5 gems. They are waiting for you and open once the game goes live. Opt-ins after that do not get them.' },
  { q: 'Does anything I do before the contest opens count?', a: 'No. Spends and actions count only while the contest is live, from 18 Oct, 6 PM, to 11 Nov, not before and not after. The one exception is Referrals: friends you refer count from 13 Oct, 7 PM.' },
  { q: 'Who can take part?', a: 'GoSats users with completed KYC who have the app. [Age and state rules to confirm.] The full rules are in the Terms and Conditions.' },
  { q: 'How do I earn gems on the Card bar?', a: 'Every ₹500 of eligible spends on your GoSats Card earns a gem. At ₹2,000 the bar is full and you earn a Gold chest.' },
  { q: 'How do I earn gems on the Vouchers bar?', a: 'Every ₹500 spent on eligible vouchers earns a gem. At ₹2,000 you earn a Gold chest. Wallet-style vouchers do not count. [Eligible brands to confirm.]' },
  { q: 'How do I earn gems on the Gold bar?', a: 'Every ₹500 of Gold you buy earns a gem, whether it is a one-time purchase or a SIP. At ₹2,000 you earn a Gold chest.' },
  { q: 'How do I earn gems on the Referrals bar?', a: 'You get 1 gem for each friend you refer. A referral counts once your friend completes a card transaction, a Gold purchase or a voucher purchase. The Referrals bar opens early, on 13 Oct at 7 PM. Four friends fill the bar and earn a Gold chest.' },
  { q: 'When does the Referrals bar open?', a: 'On 13 Oct at 7 PM, ahead of the other bars. Friends you refer from then on count toward it.' },
  { q: 'What does not count?', a: 'The SuperCoin portion of a purchase, wallet loads, wallet-style vouchers, anything outside eligible categories, and anything outside the contest days.' },
  { q: 'Is there a limit on how much I can earn?', a: 'Each bar earns one Gold chest at most. The Card, Vouchers and Gold bars stop at 4 gems each. The Referrals bar keeps earning a gem for every friend, with no limit, and earns its Gold chest at 4 friends.' },
  { q: 'When can I open my gems?', a: 'As soon as you earn them, once the game is live. Your 5 early opt-in gems open from 18 Oct, 6 PM.' },
  { q: 'Can I choose between SATs and Gold?', a: 'Yes. For gems, and for most chest prizes, you pick SATs or Gold when you open it. The rupee value is the same either way, and the amount of SATs or Gold is set at the rate when the prize is credited to your account. Devices and the 1 gm Gold prize are the exceptions.' },
  { q: 'When do chests open?', a: 'All together on reveal day, 13 Nov. They cannot be opened earlier.' },
  { q: 'Does every chest have a prize?', a: 'Yes. Some hold devices, some hold 1 gm of Gold, and the rest hold SATs or Gold.' },
  { q: 'How are the winners chosen?', a: 'By random selection on reveal day. The result is final.' },
  { q: 'What are the big prizes?', a: 'Devices, starting with an iPhone Duo.' },
  { q: 'How do I claim a device?', a: 'Open your chest on reveal day and we will ask for your delivery details. Share them within 7 days or the prize is forfeited.' },
  { q: 'Can I swap a device for cash?', a: 'No. Prizes cannot be exchanged for cash or transferred.' },
  { q: 'Where do my SATs and Gold go?', a: 'They are added to your GoSats account when you open the gem or chest. SATs are units of Bitcoin, a virtual digital asset.' },
  { q: 'Where can I read the full rules?', a: 'In the Terms and Conditions, linked from the contest page in the app.' },
];

/* "9 Days 8 Hrs 27 Mins" until launch; switches to hrs/mins/secs on the last day */
export function countdownLabel(launch = LAUNCH, now = Date.now()) {
  let d = launch.getTime() - now;
  if (d < 0) d = 0;
  const days = Math.floor(d / 864e5);
  const hrs = Math.floor((d % 864e5) / 36e5);
  const mins = Math.floor((d % 36e5) / 6e4);
  const secs = Math.floor((d % 6e4) / 1e3);
  return days > 0
    ? `${days} Days ${hrs} Hrs ${mins} Mins`
    : `${hrs} Hrs ${mins} Mins ${secs} Secs`;
}

export const pad = (n) => String(n).padStart(2, '0');

/* campaign dates are IST wherever the device is: "18 Oct" / "11 November" */
const IST = 'Asia/Kolkata';
export const fmtDay = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: IST });
export const fmtDayLong = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', timeZone: IST });
/* "18th October" for the hub pill / story copy */
const ordinal = (n) => n + ((n % 100 >= 11 && n % 100 <= 13) ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] || 'th'));
const dayParts = (d, month) => {
  const p = {};
  new Intl.DateTimeFormat('en-IN', { day: 'numeric', month, timeZone: IST }).formatToParts(d)
    .forEach(({ type, value }) => { p[type] = value; });
  return p;
};
export function fmtDayOrdinal(d) {
  const p = dayParts(d, 'long');
  return `${ordinal(Number(p.day))} ${p.month}`;
}
/* "Nov 13th" for the tile chips / story copy */
export function fmtMonDayOrdinal(d) {
  const p = dayParts(d, 'short');
  return `${p.month} ${ordinal(Number(p.day))}`;
}

/* "25 Oct, 11:20 AM" for reward timestamps; `long` gives "13 November, 8:00 PM" */
const dtFormat = (month) => new Intl.DateTimeFormat('en-IN', {
  day: 'numeric', month, hour: 'numeric', minute: '2-digit', hour12: true, timeZone: IST,
});
const DT = dtFormat('short');
const DT_LONG = dtFormat('long');
export function fmtDateTime(d, long = false) {
  const p = {};
  (long ? DT_LONG : DT).formatToParts(d).forEach(({ type, value }) => { p[type] = value; });
  return `${p.day} ${p.month}, ${p.hour}:${p.minute} ${(p.dayPeriod || '').toUpperCase()}`.trim();
}
/* "2:31 pm" — the Quote & Claim card's review / closing times (prototype xTimeFmt) */
export function fmtTime(d) {
  const p = {};
  DT.formatToParts(d).forEach(({ type, value }) => { p[type] = value; });
  return `${p.hour}:${p.minute}\u00a0${(p.dayPeriod || '').toLowerCase()}`.trim();
}

/* "5,977" */
export const fmtNum = (n) => Number(n).toLocaleString('en-IN');
